import { memo } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getNotificationConfig } from './notification-config';
import type { DynamicIslandNotification } from './types';

interface DynamicIslandNotificationItemProps {
  notification: DynamicIslandNotification;
  isCompact?: boolean;
  onDismiss?: (id: string) => void;
  onClick?: (id: string) => void;
}

/**
 * Individual notification item for Dynamic Island
 * PREMIUM design with gradient accents, glowing icons, and smooth hover states
 */
export const DynamicIslandNotificationItem = memo(function DynamicIslandNotificationItem({
  notification,
  isCompact = false,
  onDismiss,
  onClick,
}: DynamicIslandNotificationItemProps) {
  const navigate = useNavigate();
  const config = getNotificationConfig(notification.type);
  const Icon = config.icon;

  // Format timestamp
  const formatTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
  };

  const handleClick = () => {
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    onClick?.(notification.id);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss?.(notification.id);
  };

  // Handle keyboard events for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  if (isCompact) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className="w-full flex items-center gap-3.5 px-5 py-4 hover:bg-white/10 transition-all duration-300 cursor-pointer group"
        style={{
          backdropFilter: 'blur(10px)',
        }}
        aria-label={`${config.label}: ${notification.title}`}
      >
        {/* Icon with gradient glow */}
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110"
          style={{
            background: `linear-gradient(135deg, ${config.color}40, ${config.color}60)`,
            boxShadow: `0 4px 16px ${config.color}40, inset 0 1px 0 rgba(255, 255, 255, 0.2)`,
          }}
        >
          <Icon
            className="w-5 h-5 transition-all duration-300"
            style={{
              color: '#ffffff',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
            }}
            strokeWidth={2.5}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 text-left">
          <p className="text-sm font-bold text-white truncate mb-0.5 tracking-tight">
            {notification.title}
          </p>
          <p className="text-xs text-white/75 truncate leading-relaxed">
            {notification.message}
          </p>
        </div>

        {/* Time */}
        <span
          className="text-xs text-white/60 flex-shrink-0 font-semibold px-2 py-1 rounded-lg"
          style={{
            background: 'rgba(255, 255, 255, 0.08)',
          }}
        >
          {formatTime(notification.timestamp)}
        </span>

        {/* Dismiss */}
        {onDismiss && (
          <button
            onClick={handleDismiss}
            className="w-9 h-9 rounded-lg hover:bg-white/15 flex items-center justify-center transition-all duration-300 flex-shrink-0 group/dismiss"
            style={{
              backdropFilter: 'blur(10px)',
            }}
            aria-label="Descartar notificación"
          >
            <X
              className="w-4.5 h-4.5 text-white/70 group-hover/dismiss:text-white transition-colors"
              strokeWidth={2.5}
            />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="w-full flex items-start gap-4 text-left group cursor-pointer"
      aria-label={`${config.label}: ${notification.title}. ${notification.message}`}
    >
      {/* Avatar/Icon with premium styling */}
      <div className="relative flex-shrink-0">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-3"
          style={{
            background: notification.avatar
              ? `url(${notification.avatar})`
              : `linear-gradient(135deg, ${config.color}, ${adjustColorBrightness(config.color, -20)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            boxShadow: `0 8px 24px ${config.color}50, inset 0 1px 0 rgba(255, 255, 255, 0.25)`,
          }}
        >
          {!notification.avatar && (
            <Icon
              className="w-7 h-7 text-white"
              style={{
                filter: 'drop-shadow(0 2px 6px rgba(0, 0, 0, 0.3))',
              }}
              strokeWidth={2.5}
            />
          )}
        </div>
        {/* Glow ring on hover */}
        <div
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            boxShadow: `0 0 30px ${config.color}, 0 0 60px ${config.color}80`,
          }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <p className="text-base font-bold text-white leading-tight tracking-tight">
            {notification.title}
          </p>
          <span
            className="text-xs text-white/70 font-semibold px-2.5 py-1 rounded-lg flex-shrink-0"
            style={{
              background: 'rgba(255, 255, 255, 0.12)',
              boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.15)',
            }}
          >
            {formatTime(notification.timestamp)}
          </span>
        </div>

        <p className="text-sm text-white/85 leading-relaxed mb-1">
          {notification.message}
        </p>

        {notification.metadata?.courseName && (
          <p
            className="text-xs text-white/70 font-medium px-2.5 py-1 rounded-lg inline-block mt-1"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
            }}
          >
            {notification.metadata.courseName}
          </p>
        )}

        {/* Action indicator */}
        {notification.actionUrl && (
          <div className="flex items-center gap-2 mt-3">
            <div
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold text-white transition-all duration-300 group-hover:gap-3"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.15))',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 2px 8px rgba(0, 0, 0, 0.1)',
              }}
            >
              <span>Ver detalles</span>
              <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </div>
          </div>
        )}
      </div>

      {/* Dismiss button */}
      {onDismiss && (
        <button
          onClick={handleDismiss}
          className="w-9 h-9 rounded-lg hover:bg-white/15 flex items-center justify-center transition-all duration-300 flex-shrink-0 group/dismiss"
          style={{
            backdropFilter: 'blur(10px)',
          }}
          aria-label="Descartar notificación"
        >
          <X
            className="w-5 h-5 text-white/70 group-hover/dismiss:text-white group-hover/dismiss:rotate-90 transition-all duration-300"
            strokeWidth={2.5}
          />
        </button>
      )}
    </div>
  );
});

/**
 * Utility function to adjust color brightness
 */
function adjustColorBrightness(color: string, percent: number): string {
  // Simple hex color brightness adjustment
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return (
    '#' +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
}
